import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Drawer, Typography, Avatar } from '@mui/material';
import useResponsive from '../../../../../hooks/useResponsive';
import NavSection from './components/navSection';
import { NavigationItems } from './components/navConfig';
import Logo from '../../../../../shared/components/common/logo';
import { getUser, Logout, userName } from '../../../../../services/authService';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import { Scrollbar } from 'smooth-scrollbar-react';
import { TooltipWrapper } from '../../../../formUtilities/useTooltip';
import { TransactionService } from '../../../../../services/transactionsService';
import { AppRoutes } from '../../../../../constants/appRoutes';
import { buildRegionizeRoute } from '../../../../../helpers/regionRouteHelper';
import { checkPermissions } from '../../../../../helpers/checkPermissionHelper';
import { RolesTypes } from '../../../../../config/roles';

const DRAWER_WIDTH = 280;

const RootStyle = styled('div')(({ theme }) => ({
	[theme.breakpoints.up('lg')]: {
		flexShrink: 0,
		width: DRAWER_WIDTH,
	},
}));

const AccountStyle = styled('div')(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	flexDirection: 'column',
}));

DashboardSidebar.propTypes = {
	isOpenSidebar: PropTypes.bool,
	onCloseSidebar: PropTypes.func,
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
	const { pathname } = useLocation();
	const [transactionCount, setTransaction] = useState(0);

	const isDesktop = useResponsive('up', 'lg');
	const firstNameLetter = userName().charAt(0) || 'A';
	const user = getUser();
	const navigate = useNavigate();

	useEffect(() => {
		if (isOpenSidebar) {
			onCloseSidebar();
		}
		if (checkPermissions(RolesTypes.Transactions)) {
			getTransactionCount();
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	const logout = () => {
		Logout();
		navigate(buildRegionizeRoute(AppRoutes.Login));
	};

	const getTransactionCount = () => {
		const transactionService = new TransactionService();
		transactionService.getTransactionCount().then((resp) => {
			setTransaction(resp.data);
		});
	};

	const renderContent = (
		<Box
			sx={{
				height: 1,
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
			}}>
			<Box
				sx={{
					px: 2.5,
					py: 1.8,
					display: 'inline-flex',
					justifyContent: 'start',
					minHeight: '80px',
				}}>
				<Logo
					sx={{ height: 30, width: 100 }}
					url={`${buildRegionizeRoute(`${AppRoutes.Dashboard}`)}`}
				/>
			</Box>
			<Box sx={{ mx: 1 }}>
				<AccountStyle>
					<Avatar
						className="mb-2 !primary-bg"
						alt="Photo URL">
						{firstNameLetter}
					</Avatar>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							flexDirection: 'column',
						}}>
						<Typography
							variant="subtitle2"
							sx={{
								color: 'text.primary',
								fontWeight: 'bold',
							}}>
							{userName()}
						</Typography>
						<Typography
							variant="caption"
							className="!font-bold"
							sx={{ color: 'text.secondary' }}>
							{user.role}
						</Typography>
						<Box className="cursor-pointer">
							<TooltipWrapper
								text={'Logout'}
								child={
									<PowerSettingsNewIcon
										onClick={() => {
											logout();
										}}
										className="!text-sm text-primary"></PowerSettingsNewIcon>
								}></TooltipWrapper>
						</Box>
					</Box>
				</AccountStyle>
			</Box>

			<Scrollbar
				plugins={{
					overscroll: {
						effect: 'bounce',
					},
				}}>
				<Box sx={{ height: 1, overflow: 'auto' }}>
					<NavSection
						navConfig={NavigationItems({ transactionCount: transactionCount })}
					/>
				</Box>
			</Scrollbar>

			<Box sx={{ flexGrow: 1 }} />
		</Box>
	);

	return (
		<RootStyle>
			{!isDesktop && (
				<Drawer
					open={isOpenSidebar}
					onClose={onCloseSidebar}
					PaperProps={{
						sx: { width: DRAWER_WIDTH },
					}}>
					{renderContent}
				</Drawer>
			)}

			{isDesktop && (
				<Drawer
					className="shadow-md border-0"
					open
					variant="persistent"
					PaperProps={{
						sx: {
							width: DRAWER_WIDTH,
							bgcolor: 'background.default',
						},
					}}>
					{renderContent}
				</Drawer>
			)}
		</RootStyle>
	);
}
